import { useEffect, useMemo, useState, useTransition } from 'react'

import { format } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'

import { useTravelDates } from './dates/useTravelDates'
import { useRoutes } from './routes/useRoutes'
import { useVehicles } from './vehicles/useVehicles'

import { useTravelSearchContext } from '../context/TravelSearchProvider'
import { getBaseApi, getLocaleFromUrlOrQuery } from '../Utils/common'
import { fetcher } from '../Utils/fetcher'

const baseApi = getBaseApi()

export function useBookingUrl(currency?: string, offercode?: string): UseBookingUrlResult {
  // Instead of local boolean, we use React concurrency
  const [isPending, startTransition] = useTransition()
  const [bookingUrl, setBookingUrl] = useState<string>('')

  const locale = getLocaleFromUrlOrQuery()
  const {
    state: { dayTripOfferCode, isDayTrip, isOneWay, residentVehicleChecked },
  } = useTravelSearchContext()

  // gather from sub-hooks
  const { routesApiData, selectedRoute } = useRoutes()
  const { multipleVehicles, selectedPassengers, selectedVehicle } = useVehicles()
  const { selectedHomeboundDate, selectedOutboundDate } = useTravelDates()

  // build URL search params
  const routeCode = selectedRoute?.routeCode
  const vehicleCode = selectedVehicle?.code
  const trailerCode = selectedVehicle?.trailer?.code
  const multipleVehiclesChecked = multipleVehicles
  const vehicleCount = selectedVehicle?.count

  const adults = selectedPassengers?.adults ?? 1
  const children = selectedPassengers?.children ?? 0
  const childAges = useMemo(() => selectedPassengers?.childrenAges ?? [], [selectedPassengers?.childrenAges])
  const pets = selectedPassengers?.pets ?? 0

  const outboundDate = selectedOutboundDate
  const homeboundDate = selectedHomeboundDate

  let offerOrProductCode = routesApiData?.offerOrProductCode
  if (!offerOrProductCode && offercode) {
    offerOrProductCode = offercode
  }

  const bookingUrlApi = useMemo(() => {
    const params = new URLSearchParams()
    locale && params.append('locale', locale)
    routeCode && params.append('routeCode', routeCode)
    vehicleCode && params.append('vehicleCode', vehicleCode)
    outboundDate && params.append('outboundDate', format(outboundDate, 'yyyy-MM-dd'))
    params.append('isOneway', isOneWay && !isDayTrip ? 'true' : 'false')

    params.append('adults', adults.toString())
    offerOrProductCode && params.append('offerOrProductCode', offerOrProductCode)
    isDayTrip && params.append('offerOrProductCode', dayTripOfferCode)
    trailerCode && params.append('trailerCode', trailerCode)
    homeboundDate && params.append('homeboundDate', format(homeboundDate, 'yyyy-MM-dd'))
    if (children > 0) {
      params.append('children', children.toString())
      childAges.forEach((age: number) => params.append('childAges', age.toString()))
    }
    if (pets > 0) params.append('pets', pets.toString())
    if (currency) params.append('currency', currency)
    if (multipleVehiclesChecked) params.append('multipleVehicles', 'true')
    if (vehicleCount && vehicleCount > 1) params.append('numberOfVehicles', vehicleCount.toString())

    const { sessionId } = setSession()
    if (sessionId) params.append('sessionId', sessionId)

    return `${baseApi}/booking-url?${params.toString()}`
  }, [
    locale,
    routeCode,
    vehicleCode,
    outboundDate,
    isOneWay,
    isDayTrip,
    adults,
    offerOrProductCode,
    dayTripOfferCode,
    trailerCode,
    homeboundDate,
    children,
    childAges,
    pets,
    currency,
    multipleVehiclesChecked,
    vehicleCount,
  ])

  useEffect(() => {
    if (!outboundDate || (!isOneWay && !homeboundDate)) return
    let canceled = false

    startTransition(() => {
      fetcher<BookingResponse>(bookingUrlApi)
        .then((data) => {
          if (canceled) return

          const origin = window.location.origin
          const finalUrl = isVercelUrl(window.location.href)
            ? `${isVercelUrl(window.location.href)}/${data?.url}`
            : `${origin}/${data?.url}`

          // If it's an FRS route, data.url is used raw
          const maybeFrsUrl = data?.isSogRoute ? data.url : finalUrl
          const finalWithSrc = addSrcParamIfItExists(window.location.href, maybeFrsUrl)
          // PAXOB is the only flow that supports resident vehicle discounts
          const isPaxobFlow = data?.url?.includes('/yourbooking')
          const finalWithDiscount = residentVehicleChecked && isPaxobFlow ? addCommercialDiscountParam(finalWithSrc) : finalWithSrc
          setBookingUrl(finalWithDiscount)
        })
        .catch((err) => {
          console.error('Error fetching booking url:', err)
          setBookingUrl('')
        })
    })

    return () => {
      canceled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingUrlApi, residentVehicleChecked])

  return {
    isLoading: isPending,
    url: bookingUrl,
  }
}

export interface UseBookingUrlResult {
  isLoading: boolean
  url: string
}

interface BookingResponse {
  isSogRoute?: boolean
  url: string
}

// Utility checks
function isVercelUrl(url: string): string | undefined {
  if (url.includes('localhost:5001')) return 'https://localhost:5001'
  if (url.includes('travel-search-dev')) return 'https://www.dev.dfds.com'
  else if (url.includes('pax-travel-search') || url.includes('travel-search-test')) return 'https://www.test.dfds.com'
  else if (url.includes('app-travel-search-nonprod') || url.includes('travel-search-uat')) return 'https://www.accept.dfds.com'
  else if (url.includes('app-travel-search-prod') || url.includes('travel-search-prod')) return 'https://www.dfds.com'
  else return undefined
}

function addSrcParamIfItExists(srcUrl: string, targetUrl: string): string {
  const srcUrlObj = new URL(srcUrl)
  const targetUrlObj = new URL(targetUrl)

  const srcParam = srcUrlObj.searchParams.get('utm_source')
  if (srcParam) {
    targetUrlObj.searchParams.set('utm_source', srcParam)
  }

  return targetUrlObj.toString()
}

function addCommercialDiscountParam(targetUrl: string): string {
  const targetUrlObj = new URL(targetUrl)
  targetUrlObj.searchParams.set('commercialDiscount', 'resident')
  return targetUrlObj.toString()
}

// Session logic
const SESSION = 'session'
function setSession(): { sessionId: string } {
  const sessionIdFromSS = getSession()?.sessionId
  const sessionId = sessionIdFromSS || uuidv4()
  sessionStorage.setItem(SESSION, JSON.stringify({ sessionId }))
  return { sessionId }
}

export function getSession(): { sessionId: string } | undefined {
  return sessionStorage[SESSION] && JSON.parse(sessionStorage[SESSION])
}
